import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { format, getDaysInMonth, startOfMonth, getDay } from 'date-fns';
import { Clock, Edit2, Plus, Save, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AttendancePortalEmbed = ({ impersonatedUser, adminUser }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [attendance, setAttendance] = useState([]);
  const [leaveCalendar, setLeaveCalendar] = useState({});
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('P');
  const [isEditing, setIsEditing] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState(null);

  const statusOptions = ['P', 'UP', 'AL', 'AHD', 'UPHD', 'OH', 'RH', 'WO', 'LWP'];
  
  const statusColors = {
    P: 'bg-green-500',
    UP: 'bg-red-500',
    AL: 'bg-blue-500',
    AHD: 'bg-green-300',
    UPHD: 'bg-red-300',
    OH: 'bg-orange-500',
    RH: 'bg-purple-500',
    WO: 'bg-gray-400',
    LWP: 'bg-red-600'
  };

  const statusLabels = {
    P: 'Present',
    UP: 'Unpaid',
    AL: 'Annual Leave',
    AHD: 'Approved Half Day',
    UPHD: 'Unpaid Half Day',
    OH: 'Organization Holiday',
    RH: 'Restricted Holiday',
    WO: 'Week Off',
    LWP: 'Leave Without Pay'
  };

  useEffect(() => {
    fetchAttendance();
  }, [selectedMonth, selectedYear, impersonatedUser]);

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(
        `${API}/superadmin/impersonate/attendance/${impersonatedUser.employee_id}?month=${selectedMonth}&year=${selectedYear}`, 
        config
      );
      setAttendance(response.data.attendance || []);
      setLeaveCalendar(response.data.leave_calendar || {});
      calculateStats(response.data.attendance || []);
    } catch (error) {
      console.error('Error fetching attendance:', error);
      toast.error('Unable to fetch attendance data');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const counts = {};
    statusOptions.forEach(status => counts[status] = 0);
    data.filter(a => a.approval_status === 'approved').forEach(record => {
      if (counts[record.status] !== undefined) {
        counts[record.status]++;
      }
    });
    setStats(counts);
  };

  const getAttendanceForDate = (day) => {
    const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return attendance.find(a => a.date === dateStr);
  };

  const getLeaveForDate = (day) => {
    const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return leaveCalendar[dateStr] || null;
  };

  const handleDateClick = (day) => {
    const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const existingRecord = getAttendanceForDate(day);
    const leave = getLeaveForDate(day);
    
    // Don't allow editing dates with approved leave
    if (leave && leave.approval_status === 'approved') {
      toast.info('This date has an approved leave application');
      return;
    }
    
    setSelectedDate(dateStr);
    setSelectedStatus(existingRecord?.status || 'P');
    setIsEditing(!!existingRecord);
    setEditingRecordId(existingRecord?.id || null);
    setEditDialogOpen(true);
  };

  const handleSaveAttendance = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const payload = {
        portal: 'attendance',
        employee_id: impersonatedUser.employee_id,
        data: {
          date: selectedDate,
          status: selectedStatus,
          approval_status: 'approved', // Super Admin auto-approves
          marked_by: 'superadmin',
          marked_at: new Date().toISOString()
        }
      };

      if (isEditing && editingRecordId) {
        // Update existing entry using the proper record ID
        await axios.put(`${API}/superadmin/entries/update`, {
          ...payload,
          record_id: editingRecordId
        }, config);
        toast.success(`Attendance updated for ${selectedDate}`);
      } else {
        // Create new entry
        await axios.post(`${API}/superadmin/entries/create`, payload, config);
        toast.success(`Attendance marked for ${selectedDate}`);
      }
      
      setEditDialogOpen(false);
      fetchAttendance(); // Refresh data
    } catch (error) {
      console.error('Error saving attendance:', error);
      toast.error(error.response?.data?.detail || 'Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAttendance = async () => {
    if (!editingRecordId) {
      toast.error('Cannot delete - no record found');
      return;
    }
    
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.delete(`${API}/superadmin/entries/delete`, {
        ...config,
        data: {
          portal: 'attendance',
          employee_id: impersonatedUser.employee_id,
          record_id: editingRecordId
        }
      });
      
      toast.success(`Attendance deleted for ${selectedDate}`);
      setEditDialogOpen(false);
      fetchAttendance();
    } catch (error) {
      console.error('Error deleting attendance:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete attendance');
    } finally {
      setSaving(false);
    }
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(new Date(selectedYear, selectedMonth - 1));
    const firstDay = getDay(startOfMonth(new Date(selectedYear, selectedMonth - 1)));
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="aspect-square"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const record = getAttendanceForDate(day);
      const leave = getLeaveForDate(day);
      const hasLeave = leave !== null;
      const hasRecord = record !== null;

      days.push(
        <div
          key={day}
          onClick={() => handleDateClick(day)}
          className={`aspect-square border rounded-lg p-2 cursor-pointer transition-all hover:shadow-md hover:border-purple-400 ${
            hasLeave ? (leave.leave_type === 'LWP' ? 'bg-red-50' : leave.leave_type === 'OH' ? 'bg-orange-50' : leave.leave_type === 'RH' ? 'bg-purple-50' : 'bg-blue-50') : hasRecord ? 'bg-gray-50' : 'bg-white'
          }`}
          data-testid={`attendance-day-${day}`}
        >
          <div className="text-sm font-medium mb-1 flex items-center justify-between">
            <span>{day}</span>
            {!hasLeave && (
              <span className="text-purple-500 opacity-60 hover:opacity-100">
                {hasRecord ? <Edit2 className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
              </span>
            )}
          </div>
          
          {hasLeave && (
            <div className={`${statusColors[leave.leave_type] || 'bg-blue-500'} text-white text-xs px-1 py-0.5 rounded text-center`}>
              {leave.leave_type}
            </div>
          )}
          
          {!hasLeave && record && (
            <div className={`${statusColors[record.status]} text-white text-xs px-1 py-0.5 rounded text-center`}>
              {record.status}
              {record.approval_status === 'pending' && <Clock className="w-3 h-3 inline ml-1" />}
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="attendance-portal-embed">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                Attendance for {impersonatedUser.name}
              </CardTitle>
              <div className="flex gap-2">
                <Select value={String(selectedMonth)} onValueChange={(value) => setSelectedMonth(Number(value))}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>
                        {format(new Date(2024, i, 1), 'MMMM')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={String(selectedYear)} onValueChange={(value) => setSelectedYear(Number(value))}>
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(year => (
                      <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-2 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-600">{day}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-2">
              {renderCalendar()}
            </div>
            <p className="mt-4 text-sm text-purple-600 font-medium">
              Click on any date to mark or update attendance for {impersonatedUser.name}
            </p>
          </CardContent>
        </Card>

        {/* Statistics */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Monthly Stats</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(statusLabels).map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded ${statusColors[key]}`}></div>
                      <span className="text-sm">{label}</span>
                    </div>
                    <span className="font-semibold">{stats[key] || 0}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          
          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs text-gray-500">
                As Super Admin, your changes are auto-approved.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {isEditing ? 'Update Attendance' : 'Mark Attendance'} - {selectedDate}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Attendance Status
            </label>
            <div className="grid grid-cols-3 gap-2">
              {statusOptions.map(status => (
                <Button
                  key={status}
                  type="button"
                  variant={selectedStatus === status ? 'default' : 'outline'}
                  className={`${selectedStatus === status ? statusColors[status] + ' text-white' : ''}`}
                  onClick={() => setSelectedStatus(status)}
                >
                  {status}
                </Button>
              ))}
            </div>
            <p className="mt-2 text-sm text-gray-500">
              {statusLabels[selectedStatus]}
            </p>
          </div>

          <DialogFooter className="flex gap-2">
            {isEditing && (
              <Button
                variant="destructive"
                onClick={handleDeleteAttendance}
                disabled={saving}
              >
                Delete
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              disabled={saving}
            >
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button
              onClick={handleSaveAttendance}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttendancePortalEmbed;
